import {
  HomeIcon,
  CreditCardIcon,
  ArrowRightLeftIcon,
  SettingsIcon,
  BriefcaseIcon,
  MessageSquareIcon,
  BellIcon,
  CopyIcon,
  WalletIcon,
  StickyNoteIcon,
  PlusIcon,
  CalendarIcon,
  MailIcon,
  HardDriveIcon,
  FileTextIcon,
  TrendingUpIcon,
  CalculatorIcon,
  ReceiptIcon,
  BookmarkIcon,
  ZapIcon,
  Landmark as LandmarkIcon,
  LogOut as LogOutIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  keywords?: string[];
  /** Primary items appear in the main navbar */
  isPrimary?: boolean;
  /** Secondary items appear in the user dropdown */
  isSecondary?: boolean;
}

export interface NavigationAction {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  keywords?: string[];
}

/**
 * Centralized navigation configuration
 * This configuration is automatically used by:
 * - Navbar component (primary items)
 * - User dropdown (secondary items)
 * - Command palette (all items)
 */
const navigationItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/",
    icon: HomeIcon,
    keywords: ["home", "overview", "summary"],
    isPrimary: true,
  },
  {
    id: "accounts",
    label: "Accounts",
    path: "/accounts",
    icon: LandmarkIcon,
    keywords: ["bank", "credit card", "wallet", "balance"],
    isPrimary: true,
    isSecondary: true,
  },
  {
    id: "transactions",
    label: "Transactions",
    path: "/transactions",
    icon: ArrowRightLeftIcon,
    keywords: ["expense", "income", "payment", "transfer"],
    isPrimary: true,
  },
  {
    id: "budgets",
    label: "Budgets",
    path: "/budgets",
    icon: WalletIcon,
    keywords: ["budget", "spending", "limit", "plan"],
    isPrimary: true,
  },
  {
    id: "credit-cards",
    label: "Credit Cards",
    path: "/credit-cards",
    icon: CreditCardIcon,
    keywords: [
      "credit card",
      "cards",
      "statement",
      "bill",
      "due",
      "rewards",
      "benefits",
      "billing cycle",
      "utilization",
    ],
    isPrimary: true,
  },
  {
    id: "notes",
    label: "Notes",
    path: "/notes",
    icon: StickyNoteIcon,
    keywords: ["notes", "memo", "writing"],
    isPrimary: true,
  },
  {
    id: "bookmarks",
    label: "Bookmarks",
    path: "/bookmarks",
    icon: BookmarkIcon,
    keywords: ["bookmarks", "features", "all", "navigation", "links"],
    isPrimary: true,
  },
  {
    id: "bills",
    label: "Bills",
    path: "/bills",
    icon: CalendarIcon,
    keywords: ["bills", "subscriptions", "recurring", "payment", "due"],
  },
  {
    id: "loan-calculator",
    label: "Loan Calculator",
    path: "/loan-calculator",
    icon: CalculatorIcon,
    keywords: ["loan", "emi", "calculator", "repayment", "amortization", "interest", "debt free"],
  },
  {
    id: "workflows",
    label: "Workflows",
    path: "/workflows",
    icon: ZapIcon,
    keywords: ["workflow", "automation", "zapier", "automate", "trigger", "action", "engine"],
  },
  {
    id: "statements",
    label: "Import",
    path: "/statements",
    icon: FileTextIcon,
    keywords: ["import", "statement", "bank", "credit card", "pdf", "extract", "upload"],
  },
  {
    id: "payslips",
    label: "Payslips",
    path: "/payslips",
    icon: ReceiptIcon,
    keywords: ["payslip", "salary", "pay", "income", "earnings", "deductions"],
  },
  {
    id: "receipts",
    label: "Receipts",
    path: "/receipts",
    icon: ReceiptIcon,
    keywords: ["receipt", "invoice", "bill", "purchase", "gold", "jewellery", "jewelry"],
  },
  {
    id: "credit-reports",
    label: "Credit Reports",
    path: "/credit-reports",
    icon: TrendingUpIcon,
    keywords: ["credit", "score", "cibil", "experian", "crif", "report", "bureau"],
  },
  {
    id: "duplicates",
    label: "Duplicates",
    path: "/duplicates",
    icon: CopyIcon,
    keywords: ["duplicate", "repeated", "same"],
    isSecondary: true,
  },
  {
    id: "jobs",
    label: "Jobs",
    path: "/jobs",
    icon: BriefcaseIcon,
    keywords: ["background", "queue", "processing"],
    isPrimary: true,
    isSecondary: true,
  },
  {
    id: "transactional-sms",
    label: "SMS",
    path: "/transactional-sms",
    icon: MessageSquareIcon,
    keywords: ["sms", "messages", "text"],
    isSecondary: true,
  },
  {
    id: "mobile-notifications",
    label: "Notifications",
    path: "/mobile-notifications",
    icon: BellIcon,
    keywords: ["notifications", "alerts", "push"],
    isSecondary: true,
  },
  {
    id: "inbound-emails",
    label: "Emails",
    path: "/inbound-emails",
    icon: MailIcon,
    keywords: ["email", "mail", "inbox", "inbound"],
    isSecondary: true,
  },
  {
    id: "storage",
    label: "Storage",
    path: "/storage",
    icon: HardDriveIcon,
    keywords: ["storage", "s3", "files", "bucket", "upload", "download"],
    isSecondary: true,
  },
  {
    id: "settings",
    label: "Settings",
    path: "/settings",
    icon: SettingsIcon,
    keywords: ["preferences", "configuration", "options"],
    isSecondary: true,
  },
];

/**
 * Quick actions for the command palette
 */
const navigationActions: NavigationAction[] = [
  {
    id: "new-account",
    label: "Create New Account",
    path: "/accounts/new",
    icon: PlusIcon,
    keywords: ["add", "create", "new", "account", "bank"],
  },
  {
    id: "new-bill",
    label: "Create New Bill",
    path: "/bills/new",
    icon: PlusIcon,
    keywords: ["add", "create", "new", "bill", "subscription", "recurring"],
  },
];

/**
 * Get bookmarkable items (all items except primary navigation and bookmarks itself)
 */
export const getBookmarkableItems = () =>
  navigationItems
    .filter((item) => !item.isPrimary || item.id === "bookmarks")
    .filter((item) => item.id !== "bookmarks");

/* -------------------------------------------------------------------------- */
/* Shell-shape selectors (map the central config to the groot shell props)    */
/* -------------------------------------------------------------------------- */

import type { NavItem } from "@groot/shell/components/SidebarNav";
import type { UserMenuItem } from "@groot/shell/components/Layout";
import type { CommandGroupEntry } from "@groot/shell/components/CommandPalette";

/**
 * Sidebar nav items — the primary navigation entries. The dashboard entry is
 * marked `exact` so it only highlights on `/`.
 */
export function getSidebarNavItems(): NavItem[] {
  return navigationItems
    .filter((item) => item.isPrimary)
    .map((item) => ({
      name: item.label,
      href: item.path,
      icon: item.icon,
      exact: item.path === "/",
    }));
}

/**
 * Footer user-menu entries — secondary navigation + external operator links +
 * logout. `navigate` and `logout` are wired in by the app (hooks live there).
 */
export function getUserMenuItems(logout: () => void): UserMenuItem[] {
  const secondary = navigationItems
    .filter((item) => item.isSecondary)
    .map((item) => ({
      label: item.label,
      icon: item.icon,
      to: item.path,
    }));
  return [
    ...secondary,
    {
      separatorBefore: true,
      label: "Supabase Dashboard",
      href: "https://supabase.com/dashboard/project/vxavyhxpqzbwtzaapkio/editor",
    },
    {
      label: "Coolify - API App",
      href: "https://dev.nesin.io/project/rkwswsw/production/application/dokwsk4",
    },
    {
      label: "Axiom Log",
      href: "https://app.axiom.co/nesins-sandbox-mpfa/stream/personal-finance-app?caseSensitive=0&ig=&q=",
    },
    {
      separatorBefore: true,
      label: "Log out",
      icon: LogOutIcon,
      onSelect: logout,
      variant: "destructive",
    },
  ];
}

/**
 * Command-palette groups — all navigation items, quick actions, and logout.
 */
export function getCommandGroups(
  navigate: (to: string) => void,
  logout: () => void,
): CommandGroupEntry[] {
  const navGroup: CommandGroupEntry = {
    heading: "Navigation",
    items: navigationItems.map((item) => ({
      label: item.label,
      icon: item.icon,
      onSelect: () => navigate(item.path),
    })),
  };
  const actionsGroup: CommandGroupEntry = {
    heading: "Actions",
    items: navigationActions.map((action) => ({
      label: action.label,
      icon: action.icon,
      onSelect: () => navigate(action.path),
    })),
  };
  const accountGroup: CommandGroupEntry = {
    heading: "Account",
    items: [
      { label: "Log out", icon: LogOutIcon, onSelect: logout, variant: "destructive" as const },
    ],
  };
  return [navGroup, actionsGroup, accountGroup];
}
